import { useQuery } from '@apollo/client';
import { CardContent, Typography } from '@mui/material';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import { MetadataItem, MetadataItemCopy } from 'components/common/metadata/metadata-item';
import { Action, ActionType } from 'components/views/company/outreach-template/types';
import { useDemoMode } from 'providers/demo-mode';
import { FC, useState } from 'react';
import { useParams } from 'react-router';
import routes from 'routes';
import {
  CampaignSchedulerFlow,
  CampaignSchedulerFlow_campaign_schedulerFlow,
  CampaignSchedulerFlowVariables,
} from 'types/apollo/CampaignSchedulerFlow';
import { CampaignSchedulerFlowQuery } from '../../_shared-queries/campaign-outreach-template.graphql';
import { CompanyOutreachTemplateExperiments } from '../outreach-template-experiments/outreach-template-experiments';

const getConfigSummary = (config: string) => {
  if (!config) {
    return undefined;
  }
  const json = JSON.parse(config);
  const result = { sms: 0, calls: 0, days: 1 };
  const days = Object.values(json.flow);

  days.forEach((day, dayIndex) => {
    const actions = Object.values(day as Action[]);

    actions.forEach((action, actionIndex) => {
      if (action) {
        switch (action.action) {
          case ActionType.SMS:
            result.sms++;
            break;
          case ActionType.CALL:
            result.calls++;
            break;
        }

        if (dayIndex < days.length - 1 || actionIndex < actions.length - 1) {
          switch (action.time) {
            case 'delay:1day':
            case 'delay:next_day':
              result.days++;
              break;
            case 'delay:3days':
              result.days += 3;
              break;
            case 'delay:1week':
            case 'delay:next_week':
              result.days += 7;
              break;
            case 'delay:2weeks':
              result.days += 14;
              break;
          }
        }
      }
    });
  });

  return `${result.sms} SMS, ${result.calls} calls, ${result.days} days`;
};

export const CampaignOutreachTemplate: FC = () => {
  const { getRandomCampaignName, isInDemoMode } = useDemoMode();
  const { campaignId: shortId } = useParams() as { campaignId: string };
  const [schedulerFlow, setSchedulerFlow] = useState(
    {} as CampaignSchedulerFlow_campaign_schedulerFlow,
  );
  const { companyId, config, id, nlpAgentId, smsGenerator } = schedulerFlow;
  const name =
    getRandomCampaignName(schedulerFlow.name ?? undefined, companyId?.toString() ?? '', shortId) ??
    '';

  useQuery<CampaignSchedulerFlow, CampaignSchedulerFlowVariables>(CampaignSchedulerFlowQuery, {
    variables: {
      campaignId: shortId,
    },
    onCompleted: (data) => {
      const sf = data.campaign?.schedulerFlow;
      if (sf) {
        setSchedulerFlow(sf);
      }
    },
  });

  const configSummary = getConfigSummary(config);

  return (
    <Container
      maxWidth="lg"
      sx={{
        p: {
          xs: 2,
          md: 3,
        },
      }}
    >
      <Card variant="outlined" sx={{ padding: '8px' }}>
        <CardContent>
          <Typography variant="h5">Outreach Template</Typography>
          <MetadataItemCopy label="Name" value={name} />
          {!isInDemoMode && <MetadataItemCopy label="NLP Agent ID" value={nlpAgentId} />}
          {smsGenerator && !isInDemoMode && (
            <MetadataItemCopy label="SMS Generator (legacy)" value={smsGenerator} />
          )}
          {companyId && id && (
            <MetadataItem
              label="Configuration"
              value={configSummary}
              to={routes.absolute.outreachTemplate.resolve({
                companyId: companyId.toString(),
                outreachTemplateId: id,
              })}
            />
          )}
        </CardContent>
      </Card>

      <Card sx={{ marginTop: 2 }}>
        <CompanyOutreachTemplateExperiments />
      </Card>
    </Container>
  );
};
